import { FC, useMemo } from 'react';
import {
  Icon,
  Tooltip,
  Typography,
} from '@data-products-and-ai/react-components';
import { RangeColor } from '@/types';

import '@/components/GradientBar/GradientBar.scss';

interface GradientBarProps {
  data: RangeColor[];
  value: number;
  label?: string;
  description?: string;
}

export const GradientBar: FC<GradientBarProps> = ({
  data,
  value,
  label,
  description,
}) => {
  const sorted = data;
  const intervalCount = Math.max(sorted.length, 1);
  const scaleTicks = useMemo(() => {
    if (sorted.length === 0) {
      return [];
    }

    return [sorted[0].range[1], ...sorted.map((d) => d.range[0])];
  }, [sorted]);

  const gradient = useMemo(() => {
    if (sorted.length === 0) {
      return 'transparent';
    }

    if (sorted.length === 1) {
      return sorted[0]?.color ?? 'transparent';
    }

    const step = 100 / (sorted.length - 1);
    const stops = sorted.map((d, i) => `${d.color} ${i * step}%`).join(', ');

    return `linear-gradient(to bottom, ${stops})`;
  }, [sorted]);

  const markerIndex = sorted.findIndex(
    (d) => value >= d.range[0] && value <= d.range[1],
  );

  const markerPosition = useMemo(() => {
    if (markerIndex < 0 || sorted.length === 0) {
      return 0;
    }

    const [low, high] = sorted[markerIndex].range;
    const clampedValue = Math.min(Math.max(value, low), high);
    const relativeInSegment =
      high === low ? 0.5 : (high - clampedValue) / (high - low);
    const segmentSize = 1 / intervalCount;
    const segmentStart = markerIndex / intervalCount;

    return (segmentStart + relativeInSegment * segmentSize) * 100;
  }, [intervalCount, markerIndex, sorted, value]);

  const markerTransform = useMemo(() => {
    if (sorted.length === 0 || markerIndex < 0) {
      return 'translateY(-50%)';
    }

    const lastRange = sorted[sorted.length - 1]?.range;
    const isZeroLowerBound = lastRange?.[0] === 0;
    const isBottomSegment = markerIndex === sorted.length - 1;
    const isAtBottom = markerPosition >= 100;

    return isZeroLowerBound && isBottomSegment && isAtBottom
      ? 'translateY(calc(-100% + 2px))'
      : 'translateY(-50%)';
  }, [markerIndex, markerPosition, sorted]);

  return (
    <div className="gradient-bar">
      {label && (
        <div className="gradient-bar__label">
          <Typography tag="textdefault_strong">{label}</Typography>
          {description && (
            <Tooltip title={description} arrow>
              <Icon
                icon="IconInformation"
                color="var(--color-default)"
                iconSize={0.8}
              />
            </Tooltip>
          )}
        </div>
      )}

      <div className="gradient-bar__content">
        <div className="gradient-bar__scale">
          {scaleTicks.map((tickValue, i) => {
            const position = (i / intervalCount) * 100;
            const translateY =
              i === 0
                ? 'translateY(0)'
                : i === scaleTicks.length - 1
                  ? 'translateY(-100%)'
                  : 'translateY(-50%)';

            return (
              <div
                key={i}
                className="gradient-bar__scale-item"
                style={{ top: `${position}%`, transform: translateY }}
              >
                <div className="gradient-bar__scale-value">{tickValue}</div>
                <div className="gradient-bar__scale-line" />
              </div>
            );
          })}
        </div>

        <div className="gradient-bar__outer">
          <div className="gradient-bar__inner" style={{ background: gradient }}>
            {markerIndex >= 0 && (
              <div
                className="gradient-bar__inner-marker"
                style={{
                  top: `${markerPosition}%`,
                  left: '40px',
                  transform: markerTransform,
                }}
              >
                <div className="gradient-bar__inner-marker-line" />
                <Typography tag="textsmall_strong">{value}</Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
